import * as Schema from "effect/Schema";

import { NonNegativeInt, TrimmedNonEmptyString } from "./baseSchemas.ts";

// Arc RM (RM-CAN-02, 2026-09-03): a BOARD is anything that renders in one
// tab of a session's deck. The registry of board KINDS lives in the World
// authority (`boards` table, seeded from backend/convex/lib/boardRegistry.js)
// and is read through neucleos_read_boards; this module carries the literal
// kinds and the projection shapes — never a second registry (DL4).
// Nestable is the DEFAULT; root-only is the exception.

export const BOARD_KINDS = [
  "goals",
  "preview",
  "terminal",
  "files",
  "file",
  "diff",
  "pull-request",
  "pull-requests",
  "agents",
  "member",
  "inbox",
  "calendar",
  "music",
  "boomgik",
  "games",
  "commerce",
  "world-commerce",
  // Can 73: the founders' Members room (docs/world-members-design.md 4.1).
  "world-members",
  "models",
  "kanban",
  "settings",
  "usage",
  "sub-session",
  "workrooms",
  "designer",
  "media",
  "websites",
  "crm",
  "delegation-gates",
  // "charter" (8–18 Sept 2026) and "tools" (10–18 Sept) retired into platform-admin
  // (NPA3); the deck store crosswalks a persisted tab of either kind at hydrate.
  "platform-admin",
  "mobile-app",
  // Step 3 M (Canvas Components design K4): the Components Workroom. A Desk names it in supportedBoardKinds only once
  // it carries this kind, so the World never sends it to an older Desk; it reaches a World only once Neucleos offers it.
  "components",
] as const;
export const BoardKind = Schema.Literals(BOARD_KINDS);
export type BoardKind = typeof BoardKind.Type;

export const BOARD_ROLES = ["founder", "member"] as const;
export const BoardRole = Schema.Literals(BOARD_ROLES);
export type BoardRole = typeof BoardRole.Type;

export const BoardTenancy = Schema.Union([
  Schema.Struct({ kind: Schema.Literal("every-world") }),
  Schema.Struct({
    kind: Schema.Literal("worlds"),
    worldKeys: Schema.Array(TrimmedNonEmptyString).check(
      Schema.makeFilter((worldKeys) => {
        if (worldKeys.length === 0) return "A worlds tenancy must name at least one World.";
        return (
          new Set(worldKeys).size === worldKeys.length ||
          "A worlds tenancy must not repeat a World."
        );
      }),
    ),
  }),
]);
export type BoardTenancy = typeof BoardTenancy.Type;

/** Builtin body identity and actual deployment bindings. Release proofs
 * identify the delivered source version separately from these targets. */
export const BoardImplementation = Schema.Struct({
  kind: Schema.Literal("builtin"),
  key: BoardKind,
  hosts: Schema.Array(
    Schema.Union([
      Schema.Struct({
        host: Schema.Literal("desk"),
        deployment: Schema.Struct({
          kind: Schema.Literal("desktop"),
          appId: TrimmedNonEmptyString,
        }),
      }),
      Schema.Struct({
        host: Schema.Literal("cockpit"),
        deployment: Schema.Struct({
          kind: Schema.Literal("web"),
          bindingId: TrimmedNonEmptyString,
          origin: TrimmedNonEmptyString,
        }),
      }),
    ]),
  ),
});
export type BoardImplementation = typeof BoardImplementation.Type;

export const BoardDefaultSelection = Schema.Struct({
  mode: Schema.Literals(["all", "none", "groups", "standing"]),
  groups: Schema.optional(Schema.Array(TrimmedNonEmptyString)),
  memberTypes: Schema.optional(Schema.Array(TrimmedNonEmptyString)),
  tags: Schema.optional(Schema.Array(TrimmedNonEmptyString)),
  query: Schema.optional(TrimmedNonEmptyString),
});
export type BoardDefaultSelection = typeof BoardDefaultSelection.Type;

export const BoardRailView = Schema.Struct({
  key: Schema.Literals(["boomgik-library", "music"]),
  accompaniesBoard: Schema.Boolean,
  independentlyAvailable: Schema.Boolean,
});
export type BoardRailView = typeof BoardRailView.Type;

/** Where a row came from (Arc SKR): seeded production, or born in a World by a member. */
export const BoardOrigin = Schema.Union([
  Schema.Struct({ kind: Schema.Literal("production") }),
  Schema.Struct({
    kind: Schema.Literal("world"),
    worldId: Schema.String,
    worldKey: TrimmedNonEmptyString,
    authorActorId: Schema.String,
    authorKey: TrimmedNonEmptyString,
  }),
]);
export type BoardOrigin = typeof BoardOrigin.Type;

/** A saved Canvas to front on entry. It grants no access to the Canvas or its bound data. */
export const BoardOpeningCanvas = Schema.Struct({
  canvasId: Schema.String.check(Schema.makeFilter(
    (value) => /^cnv_[0-9a-f]{24}$/.test(value) || "Choose a saved Canvas.",
  )),
});
export type BoardOpeningCanvas = typeof BoardOpeningCanvas.Type;

/** One registry row as the World authority serves it. A production row's key
 * is a builtin kind; a World-authored row's key is minted (`wk-…`) and its
 * `implementation.key` names the builtin board the room opens on. */
export const BoardRegistryRow = Schema.Struct({
  key: TrimmedNonEmptyString,
  title: TrimmedNonEmptyString,
  icon: TrimmedNonEmptyString,
  nestable: Schema.Boolean,
  openableAsRoom: Schema.Boolean,
  tenancy: BoardTenancy,
  allowedRoles: Schema.Array(BoardRole),
  capability: Schema.NullOr(TrimmedNonEmptyString),
  doctrine: Schema.NullOr(Schema.String),
  tools: Schema.Array(TrimmedNonEmptyString),
  defaultSelection: BoardDefaultSelection,
  implementation: Schema.optional(BoardImplementation),
  openingCanvas: Schema.optional(BoardOpeningCanvas),
  railView: Schema.optional(BoardRailView),
  revision: NonNegativeInt,
  origin: Schema.optional(BoardOrigin),
  lifecycle: Schema.optional(Schema.Literals(["active", "retired"])),
  /** A paged answer lists a kind too large to fit one answer as a stub: its key, title and place stay, its
   * instructions, tools and starting context are left out, and `note` says so (W-e854ee62). Opening it reads it. */
  tooLarge: Schema.optional(Schema.Boolean),
  note: Schema.optional(Schema.String),
});
export type BoardRegistryRow = typeof BoardRegistryRow.Type;

/** The builtin board kind a row's room opens on: itself for a production row,
 * its `implementation.key` for a World-authored one. */
export const boardOpensWith = (
  row: Pick<BoardRegistryRow, "key" | "implementation">,
): BoardKind | null =>
  isBoardKind(row.key)
    ? row.key
    : row.implementation !== undefined && isBoardKind(row.implementation.key)
      ? row.implementation.key
      : null;

/** A member's own view of one board: the row plus the entitlement fold. */
export const BoardEntitledRow = Schema.Struct({
  ...BoardRegistryRow.fields,
  effective: Schema.Boolean,
  governingScope: Schema.Literals(["actor", "world", "default"]),
  roleFit: Schema.Boolean,
  capabilityFit: Schema.Boolean,
  launchable: Schema.Boolean,
  toolsGoverningScope: Schema.Literals(["actor", "world", "default"]),
  defaultSelectionGoverningScope: Schema.Literals(["actor", "world", "default"]),
});
export type BoardEntitledRow = typeof BoardEntitledRow.Type;

export const BoardRegistryReadback = Schema.Struct({
  schemaVersion: Schema.Literal("board-registry-v1"),
  worldKey: TrimmedNonEmptyString,
  boards: Schema.Array(BoardEntitledRow),
});
export type BoardRegistryReadback = typeof BoardRegistryReadback.Type;

/** The management read uses the same registry and the authority's entitlement fold. */
export const BoardMatrixEntitlement = Schema.Struct({
  key: TrimmedNonEmptyString,
  boardKey: TrimmedNonEmptyString,
  effective: Schema.Boolean,
  governingScope: Schema.Literals(["actor", "world", "default"]),
  override: Schema.NullOr(Schema.Boolean),
  worldDefault: Schema.NullOr(Schema.Boolean),
  roleFit: Schema.Boolean,
  capabilityFit: Schema.Boolean,
  launchable: Schema.Boolean,
  tools: Schema.Array(TrimmedNonEmptyString),
  toolsGoverningScope: Schema.Literals(["actor", "world", "default"]),
  defaultSelection: BoardDefaultSelection,
  defaultSelectionGoverningScope: Schema.Literals(["actor", "world", "default"]),
});
export const BoardMatrixWorld = Schema.Struct({
  worldKey: TrimmedNonEmptyString,
  name: Schema.String,
  defaults: Schema.Record(Schema.String, Schema.NullOr(Schema.Boolean)),
  profiles: Schema.optional(
    Schema.Record(
      Schema.String,
      Schema.Struct({
        tools: Schema.Array(TrimmedNonEmptyString),
        toolsGoverningScope: Schema.Literals(["world", "default"]),
        defaultSelection: BoardDefaultSelection,
        defaultSelectionGoverningScope: Schema.Literals(["world", "default"]),
      }),
    ),
  ),
  members: Schema.Array(
    Schema.Struct({
      actorKey: TrimmedNonEmptyString,
      role: BoardRole,
      boards: Schema.Array(BoardMatrixEntitlement),
    }),
  ),
});
export const BoardMatrixReadback = Schema.Struct({
  schemaVersion: Schema.Literal("board-matrix-v1"),
  operator: Schema.Struct({
    worldKey: TrimmedNonEmptyString,
    actorKey: TrimmedNonEmptyString,
  }),
  boards: Schema.Array(BoardRegistryRow),
  worlds: Schema.Array(BoardMatrixWorld),
});
export type BoardMatrixReadback = typeof BoardMatrixReadback.Type;

/** Where one kind stands in one World (W1): not offered, offered and waiting for
 * the founder, declined, accepted, made there ("home"), or reaching by the
 * pre-offer seed shim ("seeded"). A closed set; absent is never a state (DL6). */
export const BoardReach = Schema.Literals([
  "not-offered",
  "offered",
  "declined",
  "accepted",
  "home",
  "seeded",
]);
export type BoardReach = typeof BoardReach.Type;
export const boardReaches = (reach: BoardReach): boolean =>
  reach === "accepted" || reach === "home" || reach === "seeded";

const BoardKindSummary = Schema.Struct({
  key: TrimmedNonEmptyString,
  title: Schema.String,
  icon: Schema.String,
  owner: Schema.Literals(["platform", "world"]),
  homeWorldKey: Schema.NullOr(TrimmedNonEmptyString),
  opensWith: Schema.NullOr(TrimmedNonEmptyString),
  toolCount: NonNegativeInt,
  everyWorld: Schema.NullOr(Schema.Literals(["offered", "withdrawn"])),
});
export type BoardKindSummary = typeof BoardKindSummary.Type;

/** Neucleos's overview (neucleos_read_board_matrix view "summary"): every active
 * World × every kind's reach and World default, with member override counts and
 * no members or tool lists, so it stays small however many Worlds there are. */
export const BoardMatrixSummary = Schema.Struct({
  schemaVersion: Schema.Literal("board-matrix-summary-v1"),
  /** When the World read it (ms since the epoch), to show; nothing is ordered by it (each cell's version is). */
  readAt: NonNegativeInt,
  /** Never narrowed to the caller's World: the Worlds listed are every active World. */
  covers: Schema.Literal("every active World"),
  operator: Schema.Struct({
    worldKey: TrimmedNonEmptyString,
    actorKey: TrimmedNonEmptyString,
  }),
  kinds: Schema.Array(BoardKindSummary),
  worlds: Schema.Array(
    Schema.Struct({
      worldKey: TrimmedNonEmptyString,
      name: Schema.String,
      memberCount: NonNegativeInt,
      cells: Schema.Record(
        Schema.String,
        Schema.Struct({
          reach: BoardReach,
          worldDefault: Schema.NullOr(Schema.Boolean),
          on: Schema.NullOr(Schema.Boolean),
          membersOn: NonNegativeInt,
          membersOff: NonNegativeInt,
          /** How many room-access events stand behind this cell (offers and withdrawals of the kind, the World's
           * acceptances, removals and entitlement events for it). It only grows: of two answers about one cell, the
           * higher version is the newer. Membership changes are not counted. */
          version: NonNegativeInt,
        }),
      ),
    }),
  ),
});
export type BoardMatrixSummary = typeof BoardMatrixSummary.Type;
export type BoardSummaryCell = BoardMatrixSummary["worlds"][number]["cells"][string];

/** One World and one kind in full (view "cell"): what the summary cell folds,
 * the World's starting profile once, every member's setting without repeating
 * the tool list, and whether this caller may change it (the setters' own rule:
 * null, or the refusal's label). */
export const BoardCellReadback = Schema.Struct({
  schemaVersion: Schema.Literal("board-cell-v1"),
  readAt: NonNegativeInt,
  operator: Schema.Struct({
    worldKey: TrimmedNonEmptyString,
    actorKey: TrimmedNonEmptyString,
  }),
  world: Schema.Struct({ worldKey: TrimmedNonEmptyString, name: Schema.String }),
  kind: Schema.Struct({
    key: TrimmedNonEmptyString,
    title: Schema.String,
    icon: Schema.String,
    owner: Schema.Literals(["platform", "world"]),
    homeWorldKey: Schema.NullOr(TrimmedNonEmptyString),
    opensWith: Schema.NullOr(TrimmedNonEmptyString),
    tools: Schema.Array(TrimmedNonEmptyString),
    defaultSelection: BoardDefaultSelection,
  }),
  reach: BoardReach,
  everyWorld: Schema.NullOr(Schema.Literals(["offered", "withdrawn"])),
  worldDefault: Schema.NullOr(Schema.Boolean),
  on: Schema.NullOr(Schema.Boolean),
  /** The same count as this World × kind's summary cell. */
  version: NonNegativeInt,
  profile: Schema.NullOr(
    Schema.Struct({
      tools: Schema.Array(TrimmedNonEmptyString),
      toolsGoverningScope: Schema.Literals(["world", "default"]),
      defaultSelection: BoardDefaultSelection,
      defaultSelectionGoverningScope: Schema.Literals(["world", "default"]),
    }),
  ),
  members: Schema.Array(
    Schema.Struct({
      actorKey: TrimmedNonEmptyString,
      displayName: Schema.NullOr(Schema.String),
      role: BoardRole,
      override: Schema.NullOr(Schema.Boolean),
      effective: Schema.Boolean,
      launchable: Schema.Boolean,
      roleFit: Schema.Boolean,
      capabilityFit: Schema.Boolean,
      toolsGoverningScope: Schema.Literals(["actor", "world", "default"]),
      defaultSelectionGoverningScope: Schema.Literals(["actor", "world", "default"]),
    }),
  ),
  authority: Schema.NullOr(TrimmedNonEmptyString),
});
export type BoardCellReadback = typeof BoardCellReadback.Type;

export const isBoardKind = (value: unknown): value is BoardKind =>
  typeof value === "string" && (BOARD_KINDS as ReadonlyArray<string>).includes(value);

/** A sub-session's deck may only offer and render nestable kinds (RM-CAN-05). */
export const boardMayNest = (row: Pick<BoardRegistryRow, "nestable">): boolean => row.nestable;

export const boardOwnsWorld = (row: Pick<BoardRegistryRow, "tenancy">, worldKey: string): boolean =>
  row.tenancy.kind === "every-world" || row.tenancy.worldKeys.includes(worldKey);
